// Importaciones de interfaces.
import { IActivation } from '../interfaces/activation.interface';
import { ICustomer } from '../interfaces/customer.interface';
import { IEmployee } from '../interfaces/employee.interface';
import { IHost } from '../interfaces/host.interface';
import { ILicense } from '../interfaces/license.interface';

// Importaciones de modelos.
import { Customer } from './customer.model';
import { Employee } from './employee.model';
import { Host } from './host.model';
import { License } from './license.model';

/**
 * Clase que representa una activación.
 *
 * Esta clase implementa la interfaz IActivation y define
 * la estructura y comportamiento de un objeto de activación.
 * Se utiliza para gestionar la información relacionada con
 * la activación de servicios o productos para clientes específicos.
 */
export class Activation implements IActivation {
  /**
   * Identificador único de la activación.
   *
   * Este campo puede ser un número que representa el ID de la
   * activación o nulo si no está asignado.
   */
  id: number | null;

  /**
   * Empleado asociado a la activación.
   *
   * Este campo debe ser un objeto que implementa la interfaz
   * IEmployee, representando al empleado que gestiona la activación.
   */
  employee: IEmployee;

  /**
   * Cliente asociado a la activación.
   *
   * Este campo debe ser un objeto que implementa la interfaz
   * ICustomer, representando al cliente que solicita la activación.
   */
  customer: ICustomer;

  /**
   * Licencia asociada a la activación.
   *
   * Este campo debe ser un objeto que implementa la interfaz
   * ILicense, representando la licencia que se activará.
   */
  license: ILicense;

  /**
   * Host asociado a la activación.
   *
   * Este campo debe ser un objeto que implementa la interfaz
   * IHost, representando el host donde se realizará la activación.
   */
  host: IHost;

  /**
   * Número de serie de la activación.
   *
   * Este campo puede ser una cadena o nulo si no está disponible.
   */
  serial: string | null;

  /**
   * Código de activación.
   *
   * Este campo puede ser una cadena o nulo si no está disponible.
   */
  code: string | null;

  /**
   * Contraseña de activación.
   *
   * Este campo puede ser una cadena o nulo si no está disponible.
   */
  password: string | null;

  /**
   * Tipo de expiración de la activación.
   *
   * Este campo puede ser una cadena que indique el tipo de expiración
   * (por ejemplo, "Menos de un mes", "Más de un mes"). Puede ser nulo si no aplica.
   */
  expirationType: string | null;

  /**
   * Días de expiración de la activación.
   *
   * Este campo puede ser un número que indica cuántos días es válida
   * la activación, o nulo si no aplica.
   */
  expirationDays: number | null;

  /**
   * Comentario adicional sobre la activación.
   *
   * Este campo puede ser una cadena que contenga información adicional
   * o comentarios, o nulo si no hay información relevante.
   */
  comment: string | null;

  /**
   * Tipo de la activación.
   *
   * Este campo es un número que indica el tipo de la activación,
   * por ejemplo, 1 para estándar, 2 para 4.., 3 para 5.., etc.
   */
  type: number;

  /**
   * Estado de envío de la activación.
   *
   * Este campo almacena un número que indica el estado de envío
   * de la activación (por ejemplo, 0 para no enviado, 1 para enviado).
   */
  sent: number;

  /**
   * Estado de selección.
   *
   * Este campo almacena un boleano que indica el estado de selección
   * de la activación (por ejemplo, false para no seleccionada, true para seleccionada).
   */
  selected: boolean;

  /**
   * Fecha de última actualización.
   *
   * Indica el momento en que la activación fue modificada por última vez.
   * Es útil para el seguimiento de cambios y auditorías.
   */
  updatedAt: string | null;

  /**
   * Fecha y hora de creación.
   *
   * Representada como una cadena de texto en formato ISO 8601 (por ejemplo, "2024-12-20T10:00:00Z").
   * Esta propiedad indica cuándo fue creada la activación.
   */
  createdAt: string | null;

  /**
   * Constructor de la clase que inicializa una instancia de IActivation.
   *
   * @param {Partial<IActivation>} activation - Un objeto parcial de IActivation con las siguientes propiedades opcionales:
   *   - `id` (opcional): Identificador único de la activación. Por defecto, `null`.
   *   - `employee` (opcional): Instancia del empleado asociado. Por defecto, se inicializa con una nueva instancia de `Employee()`.
   *   - `customer` (opcional): Instancia del cliente asociado. Por defecto, se inicializa con una nueva instancia de `Customer()`.
   *   - `license` (opcional): Instancia de la licencia asociada. Por defecto, se inicializa con una nueva instancia de `License()`.
   *   - `host` (opcional): Instancia del host asociado. Por defecto, se inicializa con una nueva instancia de `Host()`.
   *   - `serial` (opcional): Número de serie de la activación. Por defecto, `null`.
   *   - `code` (opcional): Código de activación. Por defecto, `null`.
   *   - `password` (opcional): Contraseña asociada a la activación. Por defecto, `null`.
   *   - `expirationType` (opcional): Tipo de expiración de la activación. Por defecto, `null`.
   *   - `expirationDays` (opcional): Número de días hasta que la activación expire. Por defecto, `null`.
   *   - `comment` (opcional): Comentario asociado a la activación. Por defecto, `null`.
   *   - `type` (opcional): Tipo de la activación. Por defecto, `1`.
   *   - `sent` (opcional): Indica si la activación ha sido enviada (0 = no enviada, 1 = enviada). Por defecto, `0`.
   *   - `selected` (opcional): Indica si la activación está seleccionada (`true` o `false`). Por defecto, `false`.
   *   - `createdAt` (opcional): Fecha de creación de la activación. Por defecto, `null`.
   */
  constructor({
    id = null,
    employee = new Employee(),
    customer = new Customer(),
    license = new License(),
    host = new Host(),
    serial = null,
    code = null,
    password = null,
    expirationType = null,
    expirationDays = null,
    comment = null,
    type = 1,
    sent = 0,
    selected = false,
    updatedAt = null,
    createdAt = null,
  }: Partial<IActivation> = {}) {
    this.id = id;
    this.employee = employee;
    this.customer = customer;
    this.license = license;
    this.host = host;
    this.serial = serial;
    this.code = code;
    this.password = password;
    this.expirationType = expirationType;
    this.expirationDays = expirationDays;
    this.comment = comment;
    this.type = type;
    this.sent = sent;
    this.selected = selected;
    this.updatedAt = updatedAt;
    this.createdAt = createdAt;
  }
}
